/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useTelephonyServersColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for defining Telephony Servers DataGrid columns.
────────────────────────────────────────────────────────────────*/

import { useMemo, useCallback } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { DataGridColumn } from '../../../hooks/useDataGrid';
import { TelephonyServer, STATUS_STYLES } from '../types';

export function useTelephonyServersColumns() {
  const { t } = useI18n();

  const renderStatusBadge = useCallback(
    (status: string) => {
      const { badge, text } = STATUS_STYLES[status] ?? STATUS_STYLES.unknown;
      return (
        <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs ${badge}`}>
          {t(text)}
        </span>
      );
    },
    [t],
  );

  const renderMonitoringBadge = useCallback(
    (enabled: boolean) =>
      enabled ? (
        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
          {t('telephonyServers.monitoring.on')}
        </span>
      ) : (
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-800">
          {t('telephonyServers.monitoring.off')}
        </span>
      ),
    [t],
  );

  const availableColumns = useMemo<DataGridColumn[]>(
    () => [
      { key: 'name', label: t('telephonyServers.serverName'), visible: true, sortable: true },
      {
        key: 'host',
        label: t('telephonyServers.host'),
        visible: true,
        sortable: true,
        render: (_value: unknown, server: TelephonyServer) => (
          <div>
            <div className="font-medium text-foreground">{server.host}</div>
            <div className="text-xs text-muted-foreground">
              {t('telephonyServers.portLabel', { port: server.port })}
            </div>
          </div>
        ),
      },
      {
        key: 'status',
        label: t('telephonyServers.serverStatus'),
        visible: true,
        sortable: true,
        render: (value: string) => renderStatusBadge(value),
      },
      {
        key: 'freeswitchStatus',
        label: t('telephonyServers.telephonyStatus'),
        visible: true,
        sortable: true,
        render: (value: string) => renderStatusBadge(value),
      },
      {
        key: 'version',
        label: t('telephonyServers.version'),
        visible: true,
        sortable: true,
        render: (value: string | undefined) =>
          value ? (
            <span className="text-sm text-foreground">{value}</span>
          ) : (
            t('telephonyServers.status.notAvailable')
          ),
      },
      {
        key: 'monitoringEnabled',
        label: t('telephonyServers.monitoringEnabled'),
        visible: true,
        sortable: true,
        render: (value: boolean) => renderMonitoringBadge(value),
      },
      {
        key: 'lastSeen',
        label: t('telephonyServers.lastSeen'),
        visible: true,
        sortable: true,
        render: (value: string | null | undefined) =>
          value ? new Date(value).toLocaleString() : t('telephonyServers.never'),
      },
      { key: 'actions', label: t('telephonyServers.actions'), visible: true, sortable: false },
    ],
    [t, renderStatusBadge, renderMonitoringBadge],
  );

  return availableColumns;
}

